import React, { ReactNode, useContext, useEffect, useState } from "react";
import { Menu } from "antd";
import KeywordTitle from "./KeywordTitle";
import "antd/dist/antd.css";
import { Chip } from "@material-ui/core";
import { useSelector } from "react-redux";
import { AppState } from "../../../store";
import { v4 as uuidv4 } from "uuid";
import { useSafetyContext } from "./SafetyProvider";
import { ContextType } from "./KeywordSelectBox";

export interface KeywordType {
  key?: string;
  title: string;
  icon?: ReactNode;
  type?: "group";
  id?: number;
  children?: KeywordType[];
  path?: string;
}

interface Props {
  preview?: boolean;
}

export interface PickKeywords {
  key: string;
  keywordPath: string;
}

const KeywordTree: React.FC<Props> = (props) => {
  const { preview } = props;
  const { pickKeywordIds, setPickKeywordIds, isProvided } = useSafetyContext();
  const keywords = useSelector((state: AppState) => state.forms.keywords);
  const [selectedKey, setSelectedKey] = useState<string | null>();
  const [selectedKeyword, setSelectedKeyword] = useState<string>(""); // 키워드 선택 클릭시 사용
  const [pickKeywords, setPickKeywords] = useState<Map<string, string>>(
    new Map<string, string>()
  ); //
  const [subEventKeywords, setSubEventKeywords] = useState<KeywordType[]>([]);

  useEffect(() => {
    if (!keywords) return;
    setSubEventKeywords(initializeKey(keywords));
  }, [keywords]);

  /**
   *    서브이벤트 가입 최종 검토 단계에서 선택한 키워드 표시
   */
  useEffect(() => {
    if (isProvided !== ContextType.FORM) return;
    if (pickKeywordIds.size == 0) return;

    const keywordPaths: Array<[string, string]> = Array.from(
      pickKeywordIds.values()
    )
      .map(buildKeywordPath)
      .filter((tuple): tuple is [string, string] => tuple !== null);

    setPickKeywords(new Map(keywordPaths));
  }, [isProvided, pickKeywordIds, subEventKeywords]);

  function buildKeywordPath(value: number): [string, string] | null {
    const keywordPath = findPathToRootById(subEventKeywords, value);
    if (!keywordPath) return null;

    const key = keywordPath[keywordPath.length - 1].key!;
    const pathStr = keywordPath.map((item) => item.title).join(" > ");
    return [key, pathStr];
  }

  /**
   *  등록 양식에서 키워드 선택
   */
  useEffect(() => {
    const keywordRoot: KeywordType[] | null = findPathToRoot(
      subEventKeywords,
      selectedKeyword
    );

    if (keywordRoot) {
      setPickKeywords((prev) => {
        const path = keywordRoot.map((item) => item.title).join(" > ");
        const key = keywordRoot[keywordRoot.length - 1].key!;
        return path !== undefined ? new Map(prev).set(key, path) : prev;
      });

      setPickKeywordIds((prev) => {
        const id = keywordRoot?.[keywordRoot.length - 1]!.id;
        return id !== undefined ? new Set(prev).add(id) : prev;
      });
    }
  }, [selectedKeyword, subEventKeywords]);

  /**
   *  myInfo 에서 선택중인 값 세팅
   */
  useEffect(() => {
    if (!pickKeywordIds || pickKeywordIds.size < 1) return;
    if (!subEventKeywords) return;
    if (isProvided != ContextType.MYINFO) return;

    const keywordPathIndex: Map<number, KeywordType[]> =
      buildKeywordPathIndex(subEventKeywords);

    const newPickKeywords = new Map<string, string>();

    for (const id of pickKeywordIds) {
      const pathData = keywordPathIndex.get(id);
      if (pathData) {
        // 각 id에 대해 경로 문자열 생성
        const keywordPath = pathData.map((item) => item.title).join(" > ");
        // 마지막 요소의 key 추출
        const selectedKey = pathData[pathData.length - 1].key;
        if (selectedKey) {
          newPickKeywords.set(selectedKey, keywordPath);
        }
      }
    }

    // 최종적으로 상태 업데이트
    setPickKeywords(newPickKeywords);
    // setSelectedKeyword()
  }, [pickKeywordIds, isProvided, subEventKeywords]);

  const buildKeywordPathIndex = (
    keywords: KeywordType[]
  ): Map<number, KeywordType[]> => {
    const map = new Map<number, KeywordType[]>();

    const traverse = (nodes: KeywordType[], path: KeywordType[] = []) => {
      for (const node of nodes) {
        const currentPath = [...path, node];
        if (node.id !== undefined) {
          map.set(node.id, currentPath);
        }
        if (node.children && node.children.length > 0) {
          traverse(node.children, currentPath);
        }
      }
    };

    traverse(keywords);
    return map;
  };

  const getLeafNodes = (keywords: KeywordType[]): KeywordType[] => {
    let leafNodes: KeywordType[] = [];
    for (const keyword of keywords) {
      // children이 없거나 빈 배열이면 최하단 노드로 간주
      if (!keyword.children || keyword.children.length === 0) {
        leafNodes.push(keyword);
      } else {
        // children이 존재하면 재귀적으로 leaf node들을 수집
        leafNodes = leafNodes.concat(getLeafNodes(keyword.children));
      }
    }
    return leafNodes;
  };

  const initializeKey = (keywords: KeywordType[]): KeywordType[] => {
    return keywords.map((parent) => {
      if (!parent.key) {
        parent.key = uuidv4();
      }
      if (parent.children) {
        initializeKey(parent.children);
      }
      return parent;
    });
  };

  const handleClick = (e: any) => {
    setSelectedKey(e.key);
  };

  const deleteMyKeyword = (keyword: [string, string]) => {
    setPickKeywordIds((prev) => {
      const targetId: number | null = findKeywordIdByKey(
        keyword[0],
        subEventKeywords
      );
      if (targetId) {
        const newSet = new Set(prev);
        newSet.delete(targetId);
        return newSet;
      }
      return prev;
    });

    setPickKeywords((prev) => {
      const newMap = new Map(prev);
      newMap.delete(keyword[0]);
      return newMap;
    });
  };

  const findKeywordIdByKey = (
    targetKey: string,
    targetKeywords: KeywordType[]
  ): number | null => {
    for (const keyword of targetKeywords) {
      // 현재 요소의 key가 targetKey와 일치하면 id를 반환합니다.
      if (keyword.key === targetKey && keyword.id) {
        return keyword.id;
      }
      // 자식 요소가 존재하면 재귀적으로 탐색합니다.
      if (keyword.children && keyword.children.length > 0) {
        const result: number | null = findKeywordIdByKey(
          targetKey,
          keyword.children
        );
        if (result !== null) {
          return result;
        }
      }
    }
    // 일치하는 요소를 찾지 못한 경우 null 반환
    return null;
  };

  const findPathToRoot = (
    data: KeywordType[],
    targetKey: string,
    path: KeywordType[] = []
  ): KeywordType[] | null => {
    for (const item of data) {
      if (item.key === targetKey) {
        return [...path, item];
      }
      if (item.children) {
        const result = findPathToRoot(item.children, targetKey, [
          ...path,
          item,
        ]);
        if (result) {
          return result;
        }
      }
    }
    return null;
  };

  const findPathToRootById = (
    data: KeywordType[],
    targetId: number,
    path: KeywordType[] = []
  ): KeywordType[] | null => {
    for (const item of data) {
      if (item.id === targetId) {
        return [...path, item];
      }
      if (item.children && item.children.length > 0) {
        const result = findPathToRootById(item.children, targetId, [
          ...path,
          item,
        ]);
        if (result) {
          return result;
        }
      }
    }
    return null;
  };

  const generateKeword = (targetKey: string): string | null => {
    const path = findPathToRoot(subEventKeywords, targetKey);
    if (path) {
      return path.map((item) => item.title).join(" > ");
    }
    return null;
  };

  const renderMenuItems = (menuItems: KeywordType[]) => {
    return menuItems.map((item) => {
      if (item.children) {
        return (
          <Menu.SubMenu
            key={item.key}
            title={
              <KeywordTitle
                item={item}
                setSelectedKeyword={setSelectedKeyword}
                selectedMap={pickKeywords}
                preview={preview}
              />
            }
            style={{ minWidth: "280px" }}
          >
            {renderMenuItems(item.children)}
          </Menu.SubMenu>
        );
      }

      return (
        <Menu.Item
          key={item.key}
          style={{
            display: "flex",
            justifyContent: "space-between",
            minWidth: "280px",
          }}
        >
          <KeywordTitle
            item={item}
            setSelectedKeyword={setSelectedKeyword}
            selectedMap={pickKeywords}
            preview={preview}
          />
        </Menu.Item>
      );
    });
  };

  return (
    <>
      <Menu
        onClick={handleClick}
        mode="vertical"
        style={{ minWidth: 290, maxWidth: 330, padding: 10 }}
        triggerSubMenuAction="click"
        selectedKeys={[...pickKeywords.keys()]}
      >
        {subEventKeywords.length > 0 && renderMenuItems(subEventKeywords)}
      </Menu>
      <div>
        {[...pickKeywords].map((keyword, index) => (
          <Chip
            key={index}
            variant="outlined"
            label={keyword[1]}
            onDelete={() => (preview ? null : deleteMyKeyword(keyword))}
          />
        ))}
      </div>
    </>
  );
};

export default KeywordTree;
